import { agmsClientRequestGet } from "../apis/request";
import { parseServerTime } from "./serverTime";

/**
 * 웹 일일 그래프·혈당 CSV 를 DB 저장값(user_glucose) 기준으로 (2026-10-19).
 * - /api/graph/daily(알고리즘 + 지금 감도표로 세션 전체 재계산) 결과에서, 같은 분에 저장값이 있으면 그 값으로 바꾼다.
 *   저장값은 10분마다 '그때 감도표'로 계산돼 쌓인 값 → 감도표를 바꿔도 지난 구간 값이 안 바뀐다.
 * - 저장값이 없는 점(세션 시작 직후 워밍업 구간·아직 저장 전 최근 몇 분·저장 대상 아닌 세션)은 재계산 값 그대로.
 * - 바꾼 값으로 날짜별 평균·최고·최저(시각)·180 초과/70 미만 비율을 다시 계산(서버 GraphService 와 같은 식).
 * - 앱은 /api/graph/daily 를 그대로 쓰므로 영향 없음(웹만).
 * - 확증임상 계정(uxnct1~16) 세션만 적용. 그 외 사용자는 원래대로 알고리즘 재계산 값(10/19 사용자 결정 — 1·2차 DB 는 재계산과 100% 맞춰 둠).
 */
// 확증임상 계정 user id(uxnct1~16 — 6101~6106, 6352~6355, 6402~6407)
export const DB_VALUES_USER_IDS = [6101, 6102, 6103, 6104, 6105, 6106, 6352, 6353, 6354, 6355, 6402, 6403, 6404, 6405, 6406, 6407];
const UPPER = 180;
const LOWER = 70;

const minuteKey = (v) => {
  const m = parseServerTime(v);
  return m ? Math.floor(m.valueOf() / 60000) : null;
};
const round1 = (x) => Math.round(x * 10) / 10;

export async function fetchStoredGlucoseMap(userId, startedDt) {
  if (!startedDt || !DB_VALUES_USER_IDS.includes(Number(userId))) return new Map(); // 확증임상 계정 아님 → 재계산 값 그대로
  try {
    const stored = await agmsClientRequestGet(`/api/graph/glucose/stored?user_id=${userId}&started_dt=${encodeURIComponent(startedDt)}`);
    const map = new Map();
    (Array.isArray(stored) ? stored : []).forEach((p) => {
      const k = minuteKey(p.time);
      if (k !== null && p.glucose !== null && p.glucose !== undefined) map.set(k, Number(p.glucose));
    });
    return map;
  } catch (e) {
    return new Map(); // 저장값 조회 실패 → 재계산 값 그대로(화면은 계속 동작)
  }
}

export function applyStoredGlucose(result, storedMap) {
  if (!result || !Array.isArray(result.dayGraphData) || !storedMap || storedMap.size === 0) return result;
  const days = result.dayGraphData;
  // 화면(WeeklyChartList)이 통계 배열을 dayGraphData 와 같은 인덱스로 읽으므로 같은 인덱스로 고친다
  days.forEach((day, statIdx) => {
    const ts = day.dateTimeList || [];
    const vs = day.valueList || [];
    for (let i = 0; i < ts.length; i++) {
      const k = minuteKey(ts[i]);
      if (k !== null && storedMap.has(k)) vs[i] = storedMap.get(k);
    }
    let n = 0, total = 0, up = 0, low = 0, maxI = -1, minI = -1;
    for (let i = 0; i < vs.length; i++) {
      const v = vs[i];
      if (v === null || v === undefined) continue;
      if (v > UPPER) up++; else if (v < LOWER) low++;
      total += v; n++;
      if (maxI < 0 || v > vs[maxI]) maxI = i;
      if (minI < 0 || v < vs[minI]) minI = i;
    }
    if (n === 0) return;
    if (day.maxValue) day.maxValue = { ...day.maxValue, value: vs[maxI], created_at: ts[maxI] };
    if (day.minValue) day.minValue = { ...day.minValue, value: vs[minI], created_at: ts[minI] };
    const avg = total / n;
    if (result.avgGlucose && statIdx < result.avgGlucose.length) result.avgGlucose[statIdx] = avg > 0 ? round1(avg) : 0;
    if (result.maxGlucose && statIdx < result.maxGlucose.length) result.maxGlucose[statIdx] = round1(vs[maxI]);
    if (result.minGlucose && statIdx < result.minGlucose.length) result.minGlucose[statIdx] = round1(vs[minI]);
    if (result.exceedUpperPercent && statIdx < result.exceedUpperPercent.length) result.exceedUpperPercent[statIdx] = Math.round((up / n) * 1000) / 10;
    if (result.exceedLowerPercent && statIdx < result.exceedLowerPercent.length) result.exceedLowerPercent[statIdx] = Math.round((low / n) * 1000) / 10;
  });
  return result;
}
